// SLED-87: restore when J.P. Morgan signoff lands. Delete the temporary copy below this block, then uncomment the block.
// import type { JSX } from 'react';
// import { Button, cn, Popover } from '@c2fo/liquidity';
// import { Circle1Icon, Circle2Icon, Circle3Icon } from '@c2fo/liquidity/icons';
// import JPMorganLogo from '@/enterprise/assets/JPMorganLogo';
// import MonduLogo from '@/enterprise/assets/MonduLogo';
// import {
//   GET_STARTED_CONTENT,
//   HOW_IT_WORKS_CONTENT,
// } from '@/enterprise/features/extendedNetworkFinance/content/sharedContent';
//
// interface PartnerInfoPopoverProps {
//   buttonLabel: string;
//   content: string | readonly string[];
// }
//
// const STEP_ICONS: JSX.Element[] = [
//   <Circle1Icon key="step-1" className="h-11 w-11 shrink-0 fill-text-secondary" aria-hidden />,
//   <Circle2Icon key="step-2" className="h-11 w-11 shrink-0 fill-text-secondary" aria-hidden />,
//   <Circle3Icon key="step-3" className="h-11 w-11 shrink-0 fill-text-secondary" aria-hidden />,
// ];
//
// const PartnerInfoPopover = ({ buttonLabel, content }: PartnerInfoPopoverProps): JSX.Element => (
//   <Popover>
//     <Popover.Trigger asChild>
//       <Button variant="link" size="sm">
//         {buttonLabel}
//       </Button>
//     </Popover.Trigger>
//     <Popover.Content arrow className="max-w-80 text-center">
//       {typeof content === 'string' ? (
//         content
//       ) : (
//         <div className="space-y-4">
//           {content.map((paragraph) => (
//             <p key={paragraph}>{paragraph}</p>
//           ))}
//         </div>
//       )}
//     </Popover.Content>
//   </Popover>
// );
//
// const HowItWorks = (): JSX.Element => (
//   <div className="space-y-6">
//     <div className="space-y-4 text-center">
//       <p className="text-3xl">{HOW_IT_WORKS_CONTENT.title}</p>
//       <p className="mx-auto max-w-6xl text-lg">{HOW_IT_WORKS_CONTENT.intro}</p>
//     </div>
//
//     <div className="grid grid-cols-1 gap-6 rounded-xl bg-white p-6 shadow-sm lg:grid-cols-[minmax(0,1fr)_26rem]">
//       <div className="lg:pr-6">
//         {GET_STARTED_CONTENT.steps.map((step, index) => (
//           <div key={step.title} className="flex items-stretch gap-6 px-6">
//             {/* Spacers keep each icon centered against its own title and description while the connector lines join adjacent steps. */}
//             <div className="flex w-11 shrink-0 flex-col items-center">
//               <div className={cn('w-px flex-1 bg-stroke', { invisible: index === 0 })} />
//               {STEP_ICONS[index]}
//               <div
//                 className={cn('w-px flex-1 bg-stroke', {
//                   invisible: index === GET_STARTED_CONTENT.steps.length - 1,
//                 })}
//               />
//             </div>
//             <div className="space-y-1 py-6">
//               <p className="text-xl font-bold">{step.title}</p>
//               <p className="text-base">{step.description}</p>
//             </div>
//           </div>
//         ))}
//       </div>
//
//       {/*
//         Each partner mark renders in that partner's own brand color as an arbitrary value, not a
//         Liquidity palette token: these are their trademarks, not app vocabulary. The marks themselves
//         stay `currentColor` so the widget footer can render them white on its blue bar.
//       */}
//       <div className="space-y-4 border-t border-deepGray-500/30 pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
//         <div className="flex flex-col items-center space-y-4 rounded-lg bg-lightBlue-100 px-4 py-6 text-center">
//           <p className="text-base italic text-text-secondary">{HOW_IT_WORKS_CONTENT.partners.jpMorgan.eyebrow}</p>
//           {/* J.P. Morgan brown, from their supplied RGB logo artwork. */}
//           <JPMorganLogo
//             role="img"
//             aria-label={HOW_IT_WORKS_CONTENT.partners.jpMorgan.name}
//             className="h-10 w-40 text-[#4b2c1d]"
//           />
//           <PartnerInfoPopover
//             buttonLabel={HOW_IT_WORKS_CONTENT.partners.jpMorgan.buttonLabel}
//             content={HOW_IT_WORKS_CONTENT.partners.jpMorgan.popover}
//           />
//         </div>
//
//         <div className="flex flex-col items-center space-y-4 rounded-lg bg-purple-100 px-4 py-6 text-center">
//           <p className="text-base italic text-text-secondary">{HOW_IT_WORKS_CONTENT.partners.mondu.eyebrow}</p>
//           {/* Mondu brand purple, replacing the app's `purple-500`, which was a lighter lilac. */}
//           <MonduLogo
//             role="img"
//             aria-label={HOW_IT_WORKS_CONTENT.partners.mondu.name}
//             className="h-10 w-40 text-[#8C4AFF]"
//           />
//           <PartnerInfoPopover
//             buttonLabel={HOW_IT_WORKS_CONTENT.partners.mondu.buttonLabel}
//             content={HOW_IT_WORKS_CONTENT.partners.mondu.popover}
//           />
//         </div>
//       </div>
//     </div>
//
//     <p className="text-center text-xs italic text-text-secondary">{HOW_IT_WORKS_CONTENT.disclaimer}</p>
//   </div>
// );
//
// export default HowItWorks;

import type { JSX } from 'react';
import { cn } from '@c2fo/liquidity';
import { Circle1Icon, Circle2Icon, Circle3Icon } from '@c2fo/liquidity/icons';
import MonduLogo from '@/enterprise/assets/MonduLogo';
import {
  GET_STARTED_CONTENT,
  HOW_IT_WORKS_CONTENT,
} from '@/enterprise/features/extendedNetworkFinance/content/sharedContent';

const STEP_ICONS: JSX.Element[] = [
  <Circle1Icon key="step-1" className="h-11 w-11 shrink-0 fill-text-secondary" aria-hidden />,
  <Circle2Icon key="step-2" className="h-11 w-11 shrink-0 fill-text-secondary" aria-hidden />,
  <Circle3Icon key="step-3" className="h-11 w-11 shrink-0 fill-text-secondary" aria-hidden />,
];

const HowItWorks = (): JSX.Element => (
  <div className="space-y-6">
    <div className="space-y-4 text-center">
      <p className="text-3xl">{HOW_IT_WORKS_CONTENT.title}</p>
      <p className="mx-auto max-w-6xl text-lg">{HOW_IT_WORKS_CONTENT.intro}</p>
    </div>

    <div className="grid grid-cols-1 gap-6 rounded-xl bg-white p-6 shadow-sm lg:grid-cols-[minmax(0,1fr)_26rem]">
      <div className="lg:pr-6">
        {GET_STARTED_CONTENT.steps.map((step, index) => (
          <div key={step.title} className="flex items-stretch gap-6 px-6">
            {/* Spacers keep each icon centered against its own title and description while the connector lines join adjacent steps. */}
            <div className="flex w-11 shrink-0 flex-col items-center">
              <div className={cn('w-px flex-1 bg-stroke', { invisible: index === 0 })} />
              {STEP_ICONS[index]}
              <div
                className={cn('w-px flex-1 bg-stroke', {
                  invisible: index === GET_STARTED_CONTENT.steps.length - 1,
                })}
              />
            </div>
            <div className="space-y-1 py-6">
              <p className="text-xl font-bold">{step.title}</p>
              <p className="text-base">{step.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/*
        Each partner mark renders in that partner's own brand color as an arbitrary value, not a
        Liquidity palette token: these are their trademarks, not app vocabulary. The marks themselves
        stay `currentColor` so the widget footer can render them white on its blue bar.
      */}
      <div className="border-t border-deepGray-500/30 pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
        <div className="flex h-full flex-col items-center justify-center gap-9 rounded-lg bg-purple-100 p-8">
          <div className="flex flex-col items-center gap-2">
            <p className="text-base italic text-text-secondary">{HOW_IT_WORKS_CONTENT.partners.mondu.eyebrow}</p>
            {/* Mondu brand purple, replacing the app's `purple-500`, which was a lighter lilac. */}
            <MonduLogo
              role="img"
              aria-label={HOW_IT_WORKS_CONTENT.partners.mondu.name}
              className="h-10 w-40 text-[#8C4AFF]"
            />
          </div>
          <div className="w-full space-y-1 text-left">
            <p className="text-base font-bold">{HOW_IT_WORKS_CONTENT.partners.mondu.buttonLabel}</p>
            <p className="text-sm">{HOW_IT_WORKS_CONTENT.partners.mondu.popover}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default HowItWorks;
